import { initializeApp, getApps, getApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  setPersistence,
  browserSessionPersistence,
} from 'firebase/auth';
import {
  initializeFirestore,
  getFirestore,
  doc,
  getDocFromServer,
  persistentLocalCache,
  persistentMultipleTabManager,
  memoryLocalCache,
  terminate,
  clearIndexedDbPersistence,
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';
import firebaseConfig from '../firebase-applet-config.json';


// Initialize Firebase App
export const app =
  getApps().length === 0
    ? initializeApp(firebaseConfig)
    : getApp();


// ---------------------------------------------------------------------
// Firebase Authentication
//
// ORDER MATTERS. Auth MUST be initialised before Firestore.
//
// Firestore resolves its credentials provider from the app container the
// moment it is created. If Auth has not registered yet, Firestore binds to
// a provider that never yields a token and then sends EVERY request
// unauthenticated for the lifetime of the page -- so security rules see
// request.auth == null even after the user has signed in, and every read
// and write fails with "Missing or insufficient permissions".
//
// experimentalForceLongPolling makes this worse, because Firestore opens
// its connection immediately rather than lazily.
//
// Do not move this below initializeFirestore.
// ---------------------------------------------------------------------
export const auth = getAuth(app);

/**
 * SIGNING OUT WHEN THE BROWSER CLOSES.
 *
 * Firebase keeps a sign-in in localStorage by default, which belongs to the
 * whole browser profile and survives closing it entirely. That is why a new
 * tab opened the app already signed in, and why the app stayed signed in for
 * days. On a shared or borrowed phone -- which is what a field agent is
 * often using -- the next person to open the browser was inside somebody's
 * recovery book.
 *
 * Session persistence keeps the sign-in for the life of the tab instead. A
 * refresh, a navigation, a crash and reopen all keep the person signed in;
 * closing the browser signs them out.
 */
void setPersistence(auth, browserSessionPersistence).catch((err) => {
  // Private browsing can refuse storage entirely. Sign-in still works for
  // this tab; it simply will not survive a refresh.
  console.warn('[Firebase Auth] Could not set session persistence:', err?.message || err);
});


// Firestore
let firestoreDb: ReturnType<typeof getFirestore>;

/**
 * THE LOCAL CACHE -- THE SINGLE BIGGEST SAVING ON THE BILL.
 *
 * WHAT IT WAS DOING. No cache was configured, so the SDK used its default:
 * memory only. Memory dies with the page. Every sign-in, every refresh,
 * every time an agent came back to the tab, the app re-read the same
 * documents from the server and Firestore billed for every one of them.
 *
 * WHY THAT IS EXPENSIVE HERE. An agent holds 800 to 1,500 accounts and signs
 * in about ten times a day. That is ten full re-reads of the same unchanged
 * book, per agent, per day. Across fifty agents it is the largest single line
 * in the bill, and it buys nothing -- the data was identical each time.
 *
 * WHAT THE PERSISTENT CACHE CHANGES. The documents are kept in the browser's
 * own storage, so they survive the page closing. On the next visit the
 * listener serves the screen from that copy immediately and FOR FREE, then
 * asks the server only for what has CHANGED since last time. Firestore bills
 * for the changes, not for the book. The first sign-in of the day costs what
 * it always did; the other nine cost almost nothing.
 *
 * It is also why the app now feels instant on a second visit -- the list is
 * on screen before the network has answered.
 *
 * MULTIPLE TABS. `persistentMultipleTabManager` lets several tabs share one
 * cache. Without it, the second tab is refused the cache and silently falls
 * back to reading everything from the server -- which is exactly the bill we
 * are trying to remove, and it would have happened to anyone who opened the
 * app twice.
 *
 * WHEN IT CANNOT BE USED. Private browsing, a browser with storage disabled,
 * or an old engine will refuse IndexedDB. That must not stop the app
 * working, so it falls back to the memory cache -- the behaviour that was
 * there before, no worse than today.
 */
function buildFirestore(useCache: boolean) {
  return initializeFirestore(
    app,
    {
      localCache: useCache
        ? persistentLocalCache({ tabManager: persistentMultipleTabManager() })
        : memoryLocalCache(),

      // Long polling was previously FORCED on for every browser. It is much
      // slower than the default streaming transport and was a large part of
      // the ~25 second start-up. Auto-detect turns it on only for the
      // networks that actually need it (some corporate proxies), so normal
      // connections get the fast path.
      experimentalAutoDetectLongPolling: typeof window !== 'undefined',
    },
    (firebaseConfig as any).firestoreDatabaseId
  );
}

/** True when the durable cache is actually in use, for the read counter to report. */
export let persistentCacheActive = false;

try {
  firestoreDb = buildFirestore(true);
  persistentCacheActive = true;
} catch (err: any) {
  console.warn(
    '[Firestore] Durable cache unavailable, falling back to memory:',
    err?.message || err
  );
  try {
    firestoreDb = buildFirestore(false);
  } catch {
    // initializeFirestore throws if it has already run for this app -- for
    // instance after a hot reload. The existing instance is the right one.
    firestoreDb = getFirestore(app, (firebaseConfig as any).firestoreDatabaseId);
  }
}


export const db = firestoreDb;


// Firebase Storage
export const storage = getStorage(app);


// Firebase Functions
export const functions = getFunctions(app);


// Google Provider
export const googleProvider =
  new GoogleAuthProvider();


export { firebaseConfig };


// Auth helper
export async function ensureFirebaseAuth(): Promise<void> {

  return;

}



export enum OperationType {

  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',

}



export interface FirestoreErrorInfo {

  error: string;

  operationType: OperationType;

  path: string | null;

  authInfo: {

    userId?: string | null;

    email?: string | null;

    emailVerified?: boolean | null;

    isAnonymous?: boolean | null;

  };

}



export function handleFirestoreError(
  error: unknown,
  operationType: OperationType,
  path: string | null
) {


  const errInfo: FirestoreErrorInfo = {

    error:
      error instanceof Error
        ? error.message
        : String(error),

    operationType,

    path,

    authInfo: {

      userId:
        auth.currentUser?.uid,

      email:
        auth.currentUser?.email,

      emailVerified:
        auth.currentUser?.emailVerified,

      isAnonymous:
        auth.currentUser?.isAnonymous,

    },

  };


  console.error(
    'Firestore Error:',
    JSON.stringify(errInfo)
  );


  throw new Error(
    JSON.stringify(errInfo)
  );

}



export async function testFirestoreConnection(): Promise<boolean> {

  try {

    const timeoutPromise =
      new Promise<never>((_, reject) =>

        setTimeout(
          () =>
            reject(
              new Error(
                'Firestore connection check timed out'
              )
            ),
          4000
        )

      );


    await Promise.race([

      getDocFromServer(
        doc(db, 'test', 'connection')
      ),

      timeoutPromise,

    ]);


    console.log(
      'Firebase Firestore connection verified.'
    );


    return true;


  } catch (error) {


    console.warn(
      'Firestore connection check notice:',
      error
    );


    return false;

  }

}

/**
 * WIPE THE CACHED DATA WHEN SOMEBODY DELIBERATELY SIGNS OUT.
 *
 * THE TRADE THE CACHE MAKES. Keeping documents in the browser's own storage
 * is what removes the repeated re-reads -- but it also means a copy of the
 * recovery book is sitting on that device after the page closes. A field
 * agent is often on a shared or borrowed phone, which is the same reason
 * sign-in was made to end with the browser.
 *
 * So the two cases are treated differently, on purpose:
 *
 *   - CLOSING THE BROWSER signs the person out but LEAVES the cache. They
 *     are the same person on the same device coming back later, and this is
 *     where the saving comes from.
 *
 *   - PRESSING LOG OUT wipes it. That is somebody saying they have finished,
 *     which on a shared phone usually means handing it over.
 *
 * Firestore has to be shut down before its storage can be cleared, so the
 * page must be reloaded afterwards -- which is what signing out does anyway.
 * Every step is best-effort: a failure here must never leave somebody unable
 * to sign out.
 */
export async function clearLocalDataCache(): Promise<boolean> {
  if (!persistentCacheActive) return false;
  try {
    await terminate(firestoreDb);
    await clearIndexedDbPersistence(firestoreDb);
    // True means Firestore is now SHUT DOWN. The caller must reload the page:
    // a terminated instance does not recover, and signing in again against it
    // would quietly load nothing.
    return true;
  } catch (err: any) {
    // Another tab still holding the cache is the usual reason, and it is not
    // a failure worth interrupting a sign-out for.
    console.warn('[Firestore] Could not clear the local cache:', err?.message || err);
    // Firestore may or may not have been terminated before this threw, so the
    // page is reloaded either way -- a needless reload on sign-out is
    // harmless, a dead Firestore is not.
    return true;
  }
}
